/**
 * RumDashboard.tsx — RUM (Real User Monitoring) dashboard (/dashboard/rum)
 *
 * Purpose:    Core Web Vitals gauges (LCP/FID/CLS/TTFB), session replay list,
 *             and JS snippet embed code with copy-to-clipboard.
 * Inputs:     useRumData hook (port 3837)
 * Outputs:    WebVitalsCard + SessionReplayList + JsSnippetEmbed
 * Constraints: 7-state AsyncScreen; gauges rendered with inline SVG; no recharts
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import { useRumData, type WebVital, type SessionReplay, type VitalRating } from '../../hooks/useRumData'

// ---------------------------------------------------------------------------
// Rating colours
// ---------------------------------------------------------------------------

const RATING_COLORS: Record<VitalRating, { bg: string; stroke: string; text: string }> = {
  good: { bg: 'bg-green-50 dark:bg-green-900/30', stroke: '#22c55e', text: 'text-green-700 dark:text-green-400' },
  'needs-improvement': { bg: 'bg-yellow-50 dark:bg-yellow-900/30', stroke: '#eab308', text: 'text-yellow-700 dark:text-yellow-400' },
  poor: { bg: 'bg-red-50 dark:bg-red-900/30', stroke: '#ef4444', text: 'text-red-700 dark:text-red-400' },
}

// ---------------------------------------------------------------------------
// Web Vitals gauge (radial arc via SVG)
// ---------------------------------------------------------------------------

function VitalGauge({ vital }: { vital: WebVital }) {
  const { stroke, text, bg } = RATING_COLORS[vital.rating]
  const RADIUS = 30
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS

  // LCP threshold: good < 2500ms, poor > 4000ms
  // FID threshold: good < 100ms, poor > 300ms
  // CLS threshold: good < 0.1 (score), poor > 0.25
  // TTFB threshold: good < 800ms, poor > 1800ms
  const GOOD_THRESHOLD: Record<string, number> = { LCP: 2500, FID: 100, CLS: 0.1, TTFB: 800 }
  const POOR_THRESHOLD: Record<string, number> = { LCP: 4000, FID: 300, CLS: 0.25, TTFB: 1800 }

  const good = GOOD_THRESHOLD[vital.name] ?? 100
  const poor = POOR_THRESHOLD[vital.name] ?? 200
  const clampedValue = Math.min(vital.value, poor * 1.2)
  const progress = Math.min(1, clampedValue / poor)
  const dashOffset = CIRCUMFERENCE * (1 - progress)

  return (
    <div className={`flex flex-col items-center rounded-xl border border-brand-subtle p-4 ${bg}`}>
      <svg width={80} height={80} viewBox="0 0 80 80" aria-hidden>
        {/* Background circle */}
        <circle cx={40} cy={40} r={RADIUS} fill="none" stroke="#374151" strokeWidth={6} />
        {/* Progress arc */}
        <circle
          cx={40}
          cy={40}
          r={RADIUS}
          fill="none"
          stroke={stroke}
          strokeWidth={6}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
        />
      </svg>
      <p className="mt-1 text-xl font-bold text-brand-ink">
        {vital.unit === 'score'
          ? vital.value.toFixed(3)
          : vital.value < 1000
            ? `${Math.round(vital.value)}ms`
            : `${(vital.value / 1000).toFixed(2)}s`}
      </p>
      <p className="text-sm font-semibold text-brand-secondary">{vital.name}</p>
      <span className={`mt-0.5 text-xs capitalize ${text}`}>{vital.rating.replace('-', ' ')}</span>
      <p className="mt-0.5 text-xs text-brand-muted">
        p75:{' '}
        {vital.unit === 'score'
          ? vital.p75.toFixed(3)
          : vital.p75 < 1000
            ? `${Math.round(vital.p75)}ms`
            : `${(vital.p75 / 1000).toFixed(2)}s`}
      </p>
      {/* Thresholds legend */}
      <p className="mt-1 text-xs text-brand-muted">
        good &lt;{vital.unit === 'score' ? good : good < 1000 ? `${good}ms` : `${good / 1000}s`}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Session replay list
// ---------------------------------------------------------------------------

function SessionReplayList({ sessions }: { sessions: SessionReplay[] }) {
  if (sessions.length === 0)
    return <p className="text-sm text-brand-muted">No sessions recorded yet.</p>

  return (
    <div className="overflow-hidden rounded-lg border border-brand-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-subtle bg-brand-surface/50">
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Page</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Duration</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Errors</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Recorded</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-brand-muted">Replay</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {sessions.slice(0, 20).map((s) => (
            <tr key={s.id} className="hover:bg-brand-surface-2/30">
              <td className="max-w-xs truncate px-4 py-2 text-brand-secondary">{s.pageUrl}</td>
              <td className="px-4 py-2 tabular-nums text-brand-muted">
                {Math.round(s.durationMs / 1000)}s
              </td>
              <td className="px-4 py-2">
                {s.errorCount > 0 ? (
                  <span className="rounded bg-red-50 dark:bg-red-900/40 px-1.5 py-0.5 text-xs text-red-700 dark:text-red-300">
                    {s.errorCount}
                  </span>
                ) : (
                  <span className="text-brand-muted">—</span>
                )}
              </td>
              <td className="px-4 py-2 text-xs text-brand-muted">
                {new Date(s.recordedAt).toLocaleString()}
              </td>
              <td className="px-4 py-2">
                <a
                  href={s.replayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                >
                  Play
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// JS snippet embed
// ---------------------------------------------------------------------------

function JsSnippetEmbed({ snippet }: { snippet: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="rounded-lg border border-brand-subtle bg-brand-surface/50">
      <div className="flex items-center justify-between border-b border-brand-subtle px-4 py-2">
        <span className="text-xs font-medium text-brand-muted">
          Add this snippet to your &lt;head&gt;
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded-md border border-brand-card px-3 py-1 text-xs text-brand-secondary hover:bg-brand-surface-2"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3 text-xs text-green-700 dark:text-green-300">
        <code>{snippet}</code>
      </pre>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function RumSkeleton() {
  return (
    <div className="animate-pulse" aria-hidden>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-36 rounded-xl bg-brand-surface-2" />
        ))}
      </div>
      <div className="h-32 rounded-lg bg-brand-surface-2" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RumDashboard() {
  const { state, data, error, refresh } = useRumData()

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Real User Monitoring</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Core Web Vitals, session replays, and RUM SDK embed
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void refresh() }}
          className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
        >
          Refresh
        </button>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<RumSkeleton />}
        emptyMessage="No RUM data collected yet"
        emptyAction={
          <span className="text-sm text-brand-muted">
            Embed the JS snippet below to start collecting real user data
          </span>
        }
      >
        {data && (
          <div className="space-y-8">
            {/* Web Vitals gauges */}
            <section>
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                Core Web Vitals
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {data.webVitals.map((v) => (
                  <VitalGauge key={v.name} vital={v} />
                ))}
              </div>
            </section>

            {/* JS snippet */}
            {data.jsSnippet && (
              <section>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  RUM SDK embed
                </h2>
                <JsSnippetEmbed snippet={data.jsSnippet} />
              </section>
            )}

            {/* Session replays */}
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                Session replays ({data.totalSessions} total)
              </h2>
              <SessionReplayList sessions={data.sessions} />
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
